import { Component, Inject, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { Complaint, ComplaintPosition, complaintPriorities, ComplaintsDocumentType, ComplaintsDocumentTypeDict, ComplaintsDocumentTypes, DocumentStatus, PersonsService, State, Workflow } from '@icc/b2b/shared';
import { DIALOG_DATA, DialogRef } from '@icc/ui';
import { ComplaintService } from '../complaint.service';
import { Common, parseDate } from '@icc/shared/helpers';
import { NgxPermissionsService } from 'ngx-permissions';
import { ConfigService, core, logger } from '@icc/shared';
import { Subscription } from 'rxjs';
import { SyncService, getSelectedComplaint } from '@icc/b2b/sales-document';
import { Store } from '@ngrx/store';

@Component({
    selector: 'icc-complaint-edit-dialog',
    templateUrl: './complaint-edit-dialog.component.html',
    styleUrls: ['./complaint-edit-dialog.component.scss'],
})
export class ComplaintEditDialogComponent implements OnInit, OnChanges, OnDestroy {
    statuses: DocumentStatus[] = [];
    status: DocumentStatus | undefined;

    assignedToOptions: Record<string, string> = {};
    workersList;
    assignedToFilteredOptions: Record<string, string> = {};
    complaintType: ComplaintsDocumentType = ComplaintsDocumentType.Complaint;
    documentComplaintType: ComplaintsDocumentType = ComplaintsDocumentType.Complaint;
    documentAssignedTo: string | Complaint['assignedTo'] = 'authorizedRetailer';
    documentResponsibleUser: number | null = null;
    documentAssignedUser: number | null = null;
    documentPriority: string | null = null;
    documentPurchaseDate: Date | null = null;
    documentStatus: number | null = null;
    documentName: string | null = null;
    documentComplaintOrderNumber: string | null = null;

    hasAccessTo = {
        manageComplaint: false,
        editStatus: false,
        editAssign: false,
        editComplaintOrderNumber: false,
    };

    documentTypes = ComplaintsDocumentTypes;
    documentTypesDict = ComplaintsDocumentTypeDict;
    availableDocumentTypesDict: Record<number, string> = {};
    Object = Object;
    number = Number;

    documentPriorityGroup: string;
    documentPriorityValue: number;
    documentPriorities = complaintPriorities;

    private subscriptions: Subscription[] = [];

    authorizedRetailerDistributorComplaint = false;
    distributorManufacturerComplaint = false;

    today = new Date().toISOString().slice(0, 10);

    /**
     * When true, the dialog is blocked because external changes were detected.
     * User can only close the dialog.
     */
    isBlocked = false;

    /**
     * Indicates if there are statuses blocked due to missing defect tree configuration.
     */
    hasBlockedStatusesByDefectTree = false;

    noDefectTree = false;

    /**
     * Timestamp when the dialog was opened.
     * Used to detect external changes that happened after opening.
     */
    private dialogOpenedAt = 0;

    /**
     * Tracks which form fields have been modified by the user.
     * Fields in this set will not be overwritten by external updates.
     */
    private dirtyFields = new Set<string>();

    constructor(
        private dialogRef: DialogRef,
        @Inject(DIALOG_DATA)
        public data: {
            complaint: Complaint,
            appointments: any[],
            positionsWorkflowsGroups: Map<number, ComplaintPosition[]>,
            positionsWorkflows: Map<string, Workflow | null>,
            positions: ComplaintPosition[],
        },
        private complaintService: ComplaintService,
        private personsService: PersonsService,
        private permissionsService: NgxPermissionsService,
        private configService: ConfigService,
        private syncService: SyncService,
        private store: Store<State>,
    ) {
        this.authorizedRetailerDistributorComplaint = this.data.complaint.clientName.length < 1 && this.data.complaint.authorizedRetailerCompanyId !== null;
        this.distributorManufacturerComplaint = this.data.complaint.clientName.length < 1 && this.data.complaint.authorizedRetailerCompanyId === null;
        this.subscriptions.push(
            this.permissionsService.permissions$.subscribe(() => {
                this.checkAccess();
            }),
        );
    }

    async ngOnInit() {
        // Record when dialog was opened - used to detect external changes
        this.dialogOpenedAt = Date.now();
        logger.log('[ComplaintEditDialog] Dialog opened at %d, complaintUuid=%s',
            this.dialogOpenedAt,
            this.data.complaint.uuid
        );

        this.setupExternalChangesDetection();

        this.complaintService.loadContractors();
        await this.complaintService.contractorsLoaded;
        this.assignedToOptions = this.complaintService.getAssignToOptions(this.data.complaint);
        this.assignedToFilteredOptions = this.complaintService.getFilteredAssignToOptions(this.assignedToOptions, this.data.positionsWorkflowsGroups, this.data.complaint);
        this.documentComplaintType = this.data.complaint.documentTypeId;
        this.documentAssignedTo = this.getAssignToValue();
        this.documentResponsibleUser = this.data.complaint[this.getUserType()]?.userId ?? null;
        this.documentAssignedUser = this.data.complaint[this.getUserType() + 'UserId' as 'authorizedRetailerUserId' | 'distributorUserId' | 'manufacturerUserId'] ?? null;
        this.documentPriority = this.complaintService.getPriority(this.data.complaint) ?? null;
        this.documentPriorityGroup = this.documentPriority ? this.documentPriority.slice(0, 1) : '';
        this.documentPriorityValue = this.documentPriority ? +this.documentPriority.slice(1) : 0;
        this.documentPurchaseDate = this.data.complaint.purchaseDate ? new Date(this.data.complaint.purchaseDate) : null;
        this.documentName = this.data.complaint.documentName ?? null;
        this.documentComplaintOrderNumber = this.data.complaint.complaintOrderNumber ?? null;
        await this.personsService.loaded;
        this.workersList = this.personsService.workersList;
        await this.complaintService.workflowsLoaded;
        this.availableDocumentTypesDict = this.getAvailableDocumentTypes();
        this.statuses = this.complaintService.getAllRelevantStatuses(this.data.complaint, this.data.positionsWorkflowsGroups, this.getUserType(), this.data.appointments);
        this.statuses = this.sortStatusesWithNewFirst(this.statuses);
        this.hasBlockedStatusesByDefectTree = this.statuses.some(status =>
            !this.isStatusAvailable(status.id)
        );
        this.status = this.complaintService.statuses.find((s) => {
            const userType = this.getUserType();
            if (userType === 'authorizedRetailer') {
                return s.id === +this.data.complaint.authorizedRetailerStatusId;
            } else if (userType === 'distributor') {
                return s.id === +this.data.complaint.distributorStatusId;
            }
            return s.id === +this.data.complaint.manufacturerStatusId;
        });
        this.documentStatus = this.status?.id ?? null;
        this.noDefectTree = Array.from(this.data.positionsWorkflows.values()).some((workflow) => !workflow);
    }

    async ngOnChanges() {
        this.complaintService.loadContractors();
        await this.complaintService.contractorsLoaded;
        this.assignedToOptions = this.complaintService.getAssignToOptions(this.data.complaint);
        this.assignedToFilteredOptions = this.complaintService.getFilteredAssignToOptions(this.assignedToOptions, this.data.positionsWorkflowsGroups, this.data.complaint);
        await this.personsService.loaded;
        this.workersList = this.personsService.workersList;
        this.statuses = this.complaintService.getAllRelevantStatuses(this.data.complaint, this.data.positionsWorkflowsGroups, this.getUserType(), this.data.appointments);
        this.status = this.complaintService.statuses.find((s) => {
            const userType = this.getUserType();
            if (userType === 'authorizedRetailer') {
                return s.id === +this.data.complaint.authorizedRetailerStatusId;
            } else if (userType === 'distributor') {
                return s.id === +this.data.complaint.distributorStatusId;
            }
            return s.id === +this.data.complaint.manufacturerStatusId;
        });
        this.checkAccess();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((s) => s.unsubscribe());
    }

    /**
     * Sets up detection of external changes from other tabs/browsers.
     * When relevant changes are detected, the dialog is blocked to prevent conflicts.
     *
     * Fields that trigger blocking (affect available statuses and permissions):
     * - assignedTo, assignedCompanyId - changes who can edit
     * - documentTypeId - changes available workflows and statuses
     * - distributorStatusId, manufacturerStatusId, authorizedRetailerStatusId - status changes
     * - priority - priority changes
     * - purchaseDate - purchase date changes
     * - documentName, complaintOrderNumber - document metadata changes
     */
    private setupExternalChangesDetection(): void {
        this.subscriptions.push(
            // Subscribe to external changes from other tabs (BroadcastChannel) or other browsers (WebSocket)
            // These are guaranteed to be from OTHER machines, never from this tab's own actions
            this.syncService.externalChanges$.subscribe(({
                documentIds,
                documentFields,
                operationTypes,
                timestamp
            }) => {
                logger.log(
                    '[ComplaintEditDialog] Received externalChanges$: timestamp=%d, dialogOpenedAt=%d, diff=%dms, docs=%s, opTypes=%s, fields=%s',
                    timestamp,
                    this.dialogOpenedAt,
                    timestamp - this.dialogOpenedAt,
                    [...documentIds].join(', '),
                    [...operationTypes].join(', '),
                    [...documentFields].join(', ')
                );

                // Ignore changes that happened before dialog was opened
                if (timestamp <= this.dialogOpenedAt) {
                    logger.log('[ComplaintEditDialog] Ignoring - timestamp before dialog opened');
                    return;
                }

                // Check if this complaint document was modified
                if (!documentIds.has(this.data.complaint.uuid)) {
                    logger.log('[ComplaintEditDialog] Ignoring - complaint not in changed documents');
                    return;
                }

                // Fields that affect this dialog's permissions or available options
                // and should trigger blocking when changed externally:
                // - assignedTo: affects hasAccessTo.* permissions
                // - documentTypeId: affects available statuses via workflows
                // - status fields: affect what statuses can be selected next
                const relevantFields = [
                    // Permission/assignment related - affects hasAccessTo.*
                    'assignedTo',
                    // Affects available statuses via workflows
                    'documentTypeId',
                    // Status changes affect available next statuses
                    'distributorStatusId',
                    'manufacturerStatusId',
                    'authorizedRetailerStatusId',
                ];

                const hasRelevantFieldChange = relevantFields.some(field =>
                    documentFields.has(field)
                );

                logger.log(
                    '[ComplaintEditDialog] Document %s in changes, hasRelevantFieldChange=%s, changedFields=%s',
                    this.data.complaint.uuid,
                    hasRelevantFieldChange,
                    [...documentFields].join(', ')
                );

                if (hasRelevantFieldChange) {
                    logger.log('[ComplaintEditDialog] BLOCKING - relevant field changed externally');
                    this.isBlocked = true;
                }
            }),

            // Subscribe to complaint document changes from store - always update data
            this.store.select(getSelectedComplaint).subscribe((complaint) => {
                if (complaint && complaint.uuid === this.data.complaint.uuid) {
                    // Update complaint data for display
                    Object.assign(this.data.complaint, complaint);

                    // If not blocked, also update form values and available options
                    // Only update fields that haven't been modified by the user
                    if (!this.isBlocked) {
                        // Update form values that may have changed externally (only if not dirty)
                        if (!this.dirtyFields.has('documentName')) {
                            this.documentName = this.data.complaint.documentName ?? null;
                        }
                        if (!this.dirtyFields.has('documentComplaintOrderNumber')) {
                            this.documentComplaintOrderNumber = this.data.complaint.complaintOrderNumber ?? null;
                        }
                        if (!this.dirtyFields.has('documentPurchaseDate')) {
                            this.documentPurchaseDate = this.data.complaint.purchaseDate ? new Date(this.data.complaint.purchaseDate) : null;
                        }
                        if (!this.dirtyFields.has('documentPriority')) {
                            this.documentPriority = this.complaintService.getPriority(this.data.complaint) ?? null;
                            this.documentPriorityGroup = this.documentPriority ? this.documentPriority.slice(0, 1) : '';
                            this.documentPriorityValue = this.documentPriority ? +this.documentPriority.slice(1) : 0;
                        }
                        if (!this.dirtyFields.has('documentResponsibleUser')) {
                            this.documentResponsibleUser = this.data.complaint[this.getUserType()]?.userId ?? null;
                        }
                        if (!this.dirtyFields.has('documentAssignedUser')) {
                            this.documentAssignedUser = this.data.complaint[this.getUserType() + 'UserId' as 'authorizedRetailerUserId' | 'distributorUserId' | 'manufacturerUserId'] ?? null;
                        }

                        // Update available statuses based on new complaint data
                        this.statuses = this.complaintService.getAvailableStatuses(
                            this.data.complaint,
                            this.data.positions,
                            this.data.positionsWorkflowsGroups,
                            this.data.appointments,
                            this.getUserType()
                        );
                    }
                }
            }),
        );
    }

    /**
     * Marks a form field as dirty (modified by user).
     * Dirty fields will not be overwritten by external updates.
     */
    markFieldDirty(fieldName: string): void {
        this.dirtyFields.add(fieldName);
    }

    async checkAccess() {
        this.hasAccessTo.manageComplaint = await this.permissionsService.hasPermission('complaints-manage') && ((this.data.complaint.assignedTo && this.data.complaint.assignedTo.startsWith(this.getUserType())) || await this.permissionsService.hasPermission('complaints-manage-manufacturer'));
        this.hasAccessTo.editComplaintOrderNumber = this.hasAccessTo.manageComplaint && this.configService.user?.user?.ActiveCompanyLicenseType === 'license';
    }

    getAssignToValue() {
        if (this.data.complaint.assignedTo === 'distributor') {
            return 'distributor-' + this.data.complaint.assignedCompanyId;
        }
        return this.data.complaint.assignedTo;
    }

    getUserType() {
        return this.complaintService.getUserType();
    }

    getAvailableDocumentTypes(): Record<number, string> {
        const availableTypes: Record<number, string> = {};
        const workflows = this.complaintService.workflows || [];

        for (const [typeId, typeName] of Object.entries(this.documentTypesDict)) {
            const hasActiveWorkflow = workflows.some(
                (workflow) => workflow.documentTypeId === +typeId && workflow.active === true
            );
            if (hasActiveWorkflow) {
                availableTypes[+typeId] = typeName;
            }
        }

        return availableTypes;
    }

    objectLength(obj: Record<string, any>) {
        return Common.isObject(obj) ? Object.keys(obj).length : 0;
    }

    allPositionsHaveConfiguration(): boolean {
        if (this.configService.user?.user?.ActiveCompanyLicenseType === 'license') {
            return true;
        }
        return this.complaintService.allPositionsHaveConfiguration(this.data.positions);
    }

    isStatusAvailable(statusId: number): boolean {
        return this.complaintService.isStatusAvailable(
            statusId,
            this.data.complaint,
            this.data.positions,
            this.data.positionsWorkflowsGroups,
            this.getUserType()
        );
    }

    changePriority(priorityGroup: string, priorityValue: number) {
        const paddedPriorityValue = priorityValue.toString().padStart(2, '0');
        const priority = priorityGroup + paddedPriorityValue;
        this.complaintService.changePriority(this.data.complaint, priority);
    }

    async save() {
        const normalizeToDate = (date: Date | null): number | null => {
            if (!date) {
                return null;
            }
            const normalizedDate = new Date(date);
            normalizedDate.setHours(0, 0, 0, 0);
            return normalizedDate.getTime();
        }

        let changedStatus = false;
        const userType = this.getUserType();
        const actualStatusId = this.getActualStatusId(userType);
        if (this.documentStatus !== actualStatusId && this.documentStatus !== null) {
            changedStatus = await this.complaintService.changeStatus(this.data.complaint, this.documentStatus, this.getUserType());
            if (!changedStatus) {
                return;
            }
        }

        const formDocumentPurchaseDate = this.documentPurchaseDate ? normalizeToDate(parseDate(this.documentPurchaseDate)) : null;
        const documentPurchaseDate = this.data.complaint.purchaseDate ? normalizeToDate(parseDate(this.data.complaint.purchaseDate)) : null;
        if (formDocumentPurchaseDate !== documentPurchaseDate) {
            this.complaintService.changePurchaseDate(this.data.complaint, this.documentPurchaseDate);
        }
        if (this.documentName !== this.data.complaint.documentName) {
            this.complaintService.changeDocumentName(this.data.complaint, this.documentName);
        }
        if (this.documentComplaintOrderNumber !== this.data.complaint.complaintOrderNumber) {
            this.complaintService.changeComplaintOrderNumber(this.data.complaint, this.documentComplaintOrderNumber);
        }
        if (this.documentComplaintType !== this.data.complaint.documentTypeId) {
            this.data.complaint.documentTypeId = this.documentComplaintType;
            this.complaintService.changeComplaintType(this.data.complaint, this.documentComplaintType);
            this.complaintType = this.documentComplaintType;
            if (userType !== 'authorizedRetailer' && this.data.complaint.authorizedRetailerStatusId) {
                const statusChanged = await this.complaintService.changeStatus(this.data.complaint, 0, 'authorizedRetailer');
                if (!statusChanged) {
                    return;
                }
            }
            if (userType !== 'distributor' && this.data.complaint.distributorStatusId) {
                const statusChanged = await this.complaintService.changeStatus(this.data.complaint, 0, 'distributor');
                if (!statusChanged) {
                    return;
                }
            }
            if (userType !== 'manufacturer' && this.data.complaint.manufacturerStatusId) {
                const statusChanged = await this.complaintService.changeStatus(this.data.complaint, 0, 'manufacturer');
                if (!statusChanged) {
                    return;
                }
            }
            changedStatus = true;
        }
        const assignedToChanged = this.documentAssignedTo !== this.getAssignToValue();
        if (assignedToChanged) {
            this.complaintService.changeAssignedTo(this.data.complaint, this.documentAssignedTo, undefined, true, this.data.positionsWorkflowsGroups);
        }
        if (this.documentResponsibleUser !== this.data.complaint[this.getUserType()]?.userId) {
            this.complaintService.changeResponsibleUser(this.data.complaint, this.documentResponsibleUser);
        }
        if (!assignedToChanged && this.documentAssignedUser !== this.data.complaint[this.getUserType() + 'UserId' as 'authorizedRetailerUserId' | 'distributorUserId' | 'manufacturerUserId']) {
            this.complaintService.changeAssignedUser(this.data.complaint, this.documentAssignedUser);
        }
        this.changePriority(this.documentPriorityGroup, this.documentPriorityValue);

        if (changedStatus && this.documentStatus) {
            this.complaintService.triggerWorkflow('status_change', this.data.complaint, {
                documentStatus: this.documentStatus,
                level: this.getUserType(),
            }, this.data.positionsWorkflowsGroups, 0, new Set());
        }

        this.dialogRef.close();
    }

    getActualStatusId(userType: string) {
        let actualStatusId = this.data.complaint.statusId;
        if (userType === 'authorizedRetailer') {
            actualStatusId = +this.data.complaint.authorizedRetailerStatusId;
        } else if (userType === 'distributor') {
            actualStatusId = +this.data.complaint.distributorStatusId;
        } else {
            actualStatusId = +this.data.complaint.manufacturerStatusId;
        }
        return actualStatusId;
    }

    close() {
        this.dialogRef.close();
    }

    async changedType() {
        const complaint = core.copy(this.data.complaint);
        complaint.documentTypeId = +this.documentComplaintType;
        if (+this.documentComplaintType !== +this.data.complaint.documentTypeId) {
            complaint.authorizedRetailerStatusId = -1;
            complaint.distributorStatusId = -1;
            complaint.manufacturerStatusId = -1;
        }
        const {positionsWorkflowsGroups} = await this.complaintService.getPositionsWorkflows(complaint, this.data.positions);
        this.data.positionsWorkflowsGroups = positionsWorkflowsGroups;
        this.statuses = this.complaintService.getAvailableStatuses(complaint, this.data.positions, this.data.positionsWorkflowsGroups, this.data.appointments, this.getUserType());
        this.statuses = this.sortStatusesWithNewFirst(this.statuses);
        this.hasBlockedStatusesByDefectTree = this.statuses.some(status =>
            !this.isStatusAvailable(status.id)
        );
        if (+this.documentComplaintType === +this.data.complaint.documentTypeId) {
            this.status = this.complaintService.statuses.find((s) => {
                const userType = this.getUserType();
                if (userType === 'authorizedRetailer') {
                    return s.id === this.data?.complaint?.authorizedRetailerStatusId;
                } else if (userType === 'distributor') {
                    return s.id === this.data?.complaint?.distributorStatusId;
                }
                return s.id === this.data?.complaint?.manufacturerStatusId;
            });
        } else {
            this.status = this.statuses[0];
        }
        this.documentStatus = this.status?.id ?? null;
    }

    private sortStatusesWithNewFirst(statuses: DocumentStatus[]): DocumentStatus[] {
        const newStatusIndex = statuses.findIndex(s => s.new === true);
        if (newStatusIndex === -1 || newStatusIndex === 0) {
            return statuses;
        }

        const newStatus = statuses[newStatusIndex];
        const reordered = [newStatus, ...statuses.slice(0, newStatusIndex), ...statuses.slice(newStatusIndex + 1)];
        return reordered;
    }
}
